import {BaseCommand} from './base.command';
import {MultiPropertySnapshot} from '../interfaces/command.interface';
import {CytoscapeService} from '../../cy-services/cytoscape.service';
import {NodeService} from '../../../api/services/node.service';
import {firstValueFrom, of} from 'rxjs';
import {catchError, defaultIfEmpty} from 'rxjs/operators';
import {UndoLabelMetadata, UndoObjectType, UndoVerb} from '../undo-label.utils';

interface MultiPropertyCommandData {
  changes: MultiPropertySnapshot[];
}

/**
 * MultiPropertyCommand handles metadata property changes for multiple elements.
 * Supports multi-selection property add/remove/update as a single atomic operation.
 */
export class MultiPropertyCommand extends BaseCommand<MultiPropertyCommandData> {
  constructor(
    private cyService: CytoscapeService,
    private nodeService: NodeService,
    changes: MultiPropertySnapshot[]
  ) {
    const count = changes.length;

    // Determine object type based on count
    const objectType: UndoObjectType = count === 1
      ? (changes[0].entityType === 'node' ? 'Node' : 'Link')
      : 'Selection';

    // Map action to verb
    const action = changes[0].action;
    let verb: UndoVerb;
    switch (action) {
      case 'add':
        verb = 'Addition';
        break;
      case 'remove':
        verb = 'Removal';
        break;
      case 'update':
        verb = 'Change';
        break;
    }

    // Determine the part label based on what changed
    const changedField = changes[0].changedField;
    let part: string;
    if (changedField === 'key') {
      part = 'Property Key';
    } else if (changedField === 'value') {
      part = 'Property Value';
    } else {
      part = 'Property';
    }

    const labelMetadata: UndoLabelMetadata = {
      object: objectType,
      verb: verb,
      part: part,
      elementCount: objectType === 'Selection' ? count : undefined
    };

    super(
      action === 'add' ? 'add-property' : action === 'remove' ? 'remove-property' : 'update-property',
      `${count}-Element Selection ${part} ${verb}`,
      {
        before: {changes},
        after: {changes}
      },
      labelMetadata
    );
  }

  async execute(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {changes} = this.snapshot.after;

    cy.batch(() => {
      changes.forEach(change => {
        const element = cy.$(`#${change.entityId}`);
        if (element.length > 0) {
          element.data('metadata', change.afterMetadata);
        }
      });
    });

    // Update on backend
    const updatePromises = changes.map(change => {
      const payload = {id: change.entityId, metadata: change.afterMetadata};

      if (change.entityType === 'node') {
        return firstValueFrom(
          this.nodeService.updateNode(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiPropertyCommand: Failed to update node', change.entityId, err);
              return of(null);
            })
          )
        );
      } else {
        return firstValueFrom(
          this.nodeService.updateLink(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiPropertyCommand: Failed to update link', change.entityId, err);
              return of(null);
            })
          )
        );
      }
    });

    await Promise.all(updatePromises);
  }

  async undo(): Promise<void> {
    const cy = this.cyService.cy;
    if (!cy) return;

    const {changes} = this.snapshot.before;

    cy.batch(() => {
      changes.forEach(change => {
        const element = cy.$(`#${change.entityId}`);
        if (element.length > 0) {
          element.data('metadata', change.beforeMetadata);
        }
      });
    });

    // Update on backend
    const updatePromises = changes.map(change => {
      const payload = {id: change.entityId, metadata: change.beforeMetadata};

      if (change.entityType === 'node') {
        return firstValueFrom(
          this.nodeService.updateNode(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiPropertyCommand: Failed to undo node update', change.entityId, err);
              return of(null);
            })
          )
        );
      } else {
        return firstValueFrom(
          this.nodeService.updateLink(payload as any).pipe(
            defaultIfEmpty(null),
            catchError(err => {
              console.warn('MultiPropertyCommand: Failed to undo link update', change.entityId, err);
              return of(null);
            })
          )
        );
      }
    });

    await Promise.all(updatePromises);
  }
}
